import { ChatMessageItem, ReferenceItem } from '@/app/services/llm';
import { Author, ConversationItem } from '@/app/services/session';
import { Marked, RendererObject } from 'marked';
import { isChinese } from '@/lib/text-utils';

export const referencePattern = /\[\[(.*?)]]/g;

export function getReferenceNumbers(str: string) {
  const regex = /\d+/g; // 匹配连续的数字
  let match;
  const numbers = [];
  while ((match = regex.exec(str)) !== null) {
    numbers.push(match[0]);
  }

  return numbers;
}

export function replaceReferenceWithLink(
  markdown: string,
  references: ReferenceItem[],
  withTitle?: boolean,
  renderHtml?: boolean,
  messageIndex?: number,
  className?: string,
) {
  let html = markdown;
  html = html?.replace(referencePattern, replaceReference);

  const renderReference: RendererObject = {
    link(_href, _title, text) {
      return `<a class="ref-link cursor-pointer ref-pointer ${className}" data-index="${Number(text) - 1}" data-message-index="${messageIndex}">${text}</a>`;
    },
  };

  if (renderHtml) {
    const marked = new Marked({
      renderer: renderReference,
      gfm: true,
      breaks: true,
      extensions: [
        {
          name: 'force-strong',
          level: 'inline',
          start(src) {
            return src.match(/\*\*/)?.index;
          },
          tokenizer(src, tokens) {
            const rule = /^\*\*((?:[^*]|(?:\*(?!\*)))+?)\*\*/;
            const match = rule.exec(src);
            if (match) {
              return {
                type: 'strong',
                raw: match[0],
                text: match[1],
                tokens: this.lexer.inlineTokens(match[1]),
              };
            }
          },
          renderer(token) {
            return `<strong>${this.parser.parseInline(token.tokens || [])}</strong>`;
          },
        },
      ],
    });
    html = marked.parse(html) as string;
  }

  function replaceReference(_match: string, number: string) {
    if (references.length === 0) return '';

    if (!isNaN(Number(number))) {
      const reference = references[Number(number) - 1];
      if (!reference) return '';
      if (withTitle) {
        return `\n> [**${reference.title}**](${reference.link})`;
      }
      return `[${number}](${reference.link})`;
    } else {
      const numbers = getReferenceNumbers(number);
      const links: string[] = [];
      numbers.forEach((num) => {
        const reference = references[Number(num) - 1];
        if (reference) {
          if (withTitle) {
            links.push(`\n> [**${reference.title}**](${reference.link})`);
          } else {
            links.push(`[${num}](${reference.link})`);
          }
        }
      });

      return links.join(' ');
    }
  }

  return html;
}

export interface ConvertedAuthor {
  first: string;
  last: string;
}

export interface ConvertedCitation {
  authors: ConvertedAuthor[];
  year: number;
  title: string;
  publisher: string;
  url: string;
}

export function convertCitation(citation: ReferenceItem): ConvertedCitation {
  let convertedAuthors: ConvertedAuthor[] = [];
  if (citation.authors) {
    convertedAuthors = citation.authors
      .map((author) => {
        if (author.name?.trim()) {
          return {
            first: '',
            last: author.name.trim(),
          };
        }
        return {
          first: author.fore_name ?? '',
          last: author.last_name ?? '',
        };
      })
      .filter((author) => author.first || author.last);
  }

  return {
    authors: convertedAuthors,
    year: citation.pub_year,
    title: citation.title,
    publisher: citation.publisher,
    url: citation.link,
  };
}

export function formatReference(
  citation: ConvertedCitation,
  style: string,
): string {
  let formattedReference = '';

  function formatAMA(citation: ConvertedCitation): string {
    let str = '';
    if (citation.authors.length) {
      const authors = citation.authors
        .filter((author) => author.first)
        .map((author) => `${author.last} ${author.first[0]}.`)
        .slice(0, 3);
      if (citation.authors.length > 3) {
        authors.push('et al');
      }
      str = `${authors.join(', ')},`;
    }

    const title = citation.title.replace(/(^\w)|(\s\w)/g, (match) =>
      match.toUpperCase(),
    );
    const year = citation.year.toString();

    return `${str} ${year}. ${title}. ${citation.publisher}. ${citation.url}`;
  }

  function formatAPA({
    authors,
    year,
    title,
    publisher,
    url,
  }: ConvertedCitation): string {
    const formattedAuthors = authors.map((author) => {
      return author.first
        ? `${author.last}, ${author.first[0]}.`
        : author.last;
    });

    return `${formattedAuthors.join(', ')} (${year}). ${title}. ${publisher}. ${url}`;
  }

  function formatMLA({
    authors,
    year,
    title,
    publisher,
    url,
  }: ConvertedCitation): string {
    let str = '';
    if (authors.length) {
      str = `${authors[0].first} ${authors[0].last}, et al.`;
    }

    return `${str} (${year}). "${title}." ${publisher}. ${url}`;
  }

  function formatNLM(citation: ConvertedCitation): string {
    // NLM风格通常会列出所有作者，但在某些情况下，如果有大量作者，可能只列出前几位后跟等号或"et al."
    let str = '';
    if (citation.authors.length) {
      const authors = citation.authors.map((author) =>
        author.first ? `${author.last} ${author.first[0]}.` : author.last,
      );
      str = `${authors.join(', ')}.`;
    }

    // 格式化标题，通常只有第一个单词的首字母大写，其余单词小写（除非是专有名词）
    const title = citation.title.replace(/\b\w/g, (l) => l.toUpperCase());

    // 期刊名称保持原样，因为NLM风格不要求期刊名称缩写
    const publisher = citation.publisher;

    // 出版年份
    const year = citation.year.toString();

    // 构造NLM风格的引文
    return `${str} ${title} ${publisher} ${year}. Available at: ${citation.url}`;
  }

  switch (style) {
    case 'AMA':
      formattedReference = formatAMA(citation);
      break;
    case 'APA':
      formattedReference = formatAPA(citation);
      break;
    case 'MLA':
      formattedReference = formatMLA(citation);
      break;
    case 'NLM':
      formattedReference = formatNLM(citation);
      break;
    default:
      throw new Error('Unsupported citation style');
  }

  return formattedReference;
}

export const deleteOutlineTitle = (outline: string, title: string) => {
  return outline.replace(
    new RegExp(`^#\\s*${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'm'),
    '',
  );
};

export const transferConversationsToChatMessage = (
  conversations: ConversationItem[],
): ChatMessageItem[] => {
  const chatMessages: ChatMessageItem[] = [];
  for (const item of conversations) {
    const isZh = isChinese(item.summary);
    chatMessages.push({
      question:
        item.llm_search_request.messages.find((item) => item.role === 'user')
          ?.content || '',
      answer: [
        {
          title: isZh ? '中文' : '英文',
          key: isZh ? 'zh' : 'en',
          content: item.summary,
        },
        {
          title: isZh ? '英文' : '中文',
          key: isZh ? 'en' : 'zh',
          content: item.summary_translation || '',
        },
      ],
      progress: 100,
      id: item.cid,
      status: 'all_done',
      references: item.ref_docs || [],
      referencesTranslation: item.ref_docs_translation || [],
      outline: item.outline_result
        ? item.outline_result
        : {
            status: 'success',
            title: '',
            outline: '',
          },
    });
  }
  return chatMessages;
};

// 缩写单个作者的名字
const abbreviateName = (author: Author): string => {
  const directName = author.name?.trim();
  if (directName) {
    return directName;
  }
  const { fore_name, last_name } = author;
  if (!fore_name || !last_name) {
    return '';
  }
  const initials = fore_name
    .split(' ')
    .map((name) => name.charAt(0).toUpperCase())
    .join('');
  return `${last_name} ${initials}`;
};

// 缩写整个作者列表的名字
export const abbreviateAuthors = (authors: Author[]): string => {
  if (authors && authors.length) {
    return authors.map((author) => abbreviateName(author)).join(', ') + '.';
  } else {
    return '';
  }
};
